import type { NoticeDataWidgetLayer } from './types.js';
import type { NoticeWidgetArticle, NoticeWidgetData } from './noticeWidgets.js';
import { cssFontFamily } from './timeWidget.js';

// =============================================================================
// 정책브리핑 정책뉴스 목록 위젯 — Station C "오늘의 정책 소식" 템플릿(A-2 시안, 2026-09-27).
// Player, 편집기 캔버스, 템플릿 미리보기가 같은 렌더러를 쓴다. 모든 치수는 layer.fontSize(기사 제목 크기)에 비례.
// =============================================================================

export const POLICY_NEWS_SOURCE_LABEL = '대한민국 정책브리핑(www.korea.kr)';
/** 네이버 검색 API 이용약관의 출처 표시 문구. */
export const NAVER_NEWS_SOURCE_LABEL = '네이버 검색결과';
export const POLICY_NEWS_MAX_ARTICLES = 6;
export const POLICY_NEWS_ACCENT = '#0080FB';
const KST_OFFSET_MS = 9 * 3_600_000;

/** 기사 목록 레이아웃을 쓰는 위젯 소스(정책브리핑·네이버 뉴스). */
export const isNewsListSource = (source: NoticeDataWidgetLayer['source']): boolean => source.kind === 'policyNews' || source.kind === 'naverNews';

/** Response boundary check shared by Player and editor: plain bounded strings and valid timestamps only. */
export function parsePolicyNewsArticles(value: unknown): NoticeWidgetArticle[] {
  if (!Array.isArray(value)) return [];
  const clean = (entry: unknown, max: number) => typeof entry === 'string' ? entry.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, max) : '';
  const articles: NoticeWidgetArticle[] = [];
  for (const item of value.slice(0, POLICY_NEWS_MAX_ARTICLES)) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const record = item as Record<string, unknown>;
    const title = clean(record['title'], 200), org = clean(record['org'], 40);
    const publishedAt = typeof record['publishedAt'] === 'string' && Number.isFinite(Date.parse(record['publishedAt'])) ? record['publishedAt'] : '';
    if (title && publishedAt) articles.push({ title, org, publishedAt });
  }
  return articles;
}

/** "09.26 16:55" in Korea time regardless of the device time zone (Korea has no DST). */
export function policyNewsTime(iso: string | null | undefined): string {
  const at = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(at)) return '';
  const kst = new Date(at + KST_OFFSET_MS), two = (value: number) => String(value).padStart(2, '0');
  return `${two(kst.getUTCMonth() + 1)}.${two(kst.getUTCDate())} ${two(kst.getUTCHours())}:${two(kst.getUTCMinutes())}`;
}

type Measure = Pick<CanvasRenderingContext2D, 'measureText'>;
/** Greedy wrap that prefers breaking at spaces (Korean keep-all feel) and ends the last line with an ellipsis. */
export function wrapCanvasText(ctx: Measure, value: string, width: number, maxLines: number): string[] {
  const fits = (line: string) => ctx.measureText(line).width <= width;
  const lines: string[] = [];
  let rest = value.replace(/\s+/g, ' ').trim();
  while (rest && lines.length < maxLines) {
    if (fits(rest)) { lines.push(rest); break; }
    const chars = Array.from(rest);
    let low = 1, high = chars.length;
    while (low < high) { const mid = Math.ceil((low + high) / 2); if (fits(chars.slice(0, mid).join(''))) low = mid; else high = mid - 1; }
    if (lines.length === maxLines - 1) {
      let count = low;
      while (count > 0 && !fits(`${chars.slice(0, count).join('').trimEnd()}…`)) count--;
      lines.push(`${chars.slice(0, count).join('').trimEnd()}…`);
      break;
    }
    const space = chars.slice(0, low + 1).lastIndexOf(' ');
    const cut = space > low * 0.5 ? space : low;
    lines.push(chars.slice(0, cut).join('').trimEnd());
    rest = chars.slice(cut).join('').trimStart();
  }
  return lines;
}

/** Divider · 부처(강조색) · 승인 시각 · 두 줄 제목을 기사마다 쌓고, 하단에 출처와 기준 시각을 둔다. */
export function drawPolicyNewsWidget(ctx: CanvasRenderingContext2D, layer: NoticeDataWidgetLayer, data: NoticeWidgetData | null): void {
  const font = Math.min(400, Math.max(8, Number.isFinite(layer.fontSize) ? layer.fontSize : 40));
  const family = cssFontFamily(layer.fontFamily);
  const metaSize = font * 0.6, metaLine = font * 0.75, gap = font * 0.25, titleLine = font * 1.3, padding = font * 0.7;
  const footerSize = font * 0.55, footerHeight = footerSize * 1.8, divider = Math.max(1, font * 0.05);
  const spacing = (value: number) => { if ('letterSpacing' in ctx) (ctx as { letterSpacing: string }).letterSpacing = `${value}px`; };
  const fitOne = (value: string, width: number) => ctx.measureText(value).width <= width ? value : wrapCanvasText(ctx, value, width, 1)[0] ?? '';
  ctx.save();
  ctx.beginPath(); ctx.rect(layer.x, layer.y, layer.width, layer.height); ctx.clip();
  if (layer.backgroundColor) { ctx.fillStyle = layer.backgroundColor; ctx.fillRect(layer.x, layer.y, layer.width, layer.height); }
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  const base = ctx.globalAlpha, bottom = layer.y + layer.height - footerHeight;
  const articles = data && data.status !== 'unavailable' ? data.articles ?? [] : [];
  const naver = layer.source.kind === 'naverNews';
  let y = layer.y;
  if (!articles.length) {
    ctx.globalAlpha = base * 0.45; ctx.fillStyle = layer.color; spacing(0);
    ctx.font = `400 ${metaSize}px ${family}`;
    ctx.fillText(fitOne(data?.message || (naver ? '뉴스를 불러오는 중입니다.' : '정책 소식을 불러오는 중입니다.'), layer.width), layer.x, y + padding + metaLine / 2);
  }
  for (const article of articles) {
    ctx.font = `700 ${font}px ${family}`; spacing(-font * 0.015);
    const lines = wrapCanvasText(ctx, article.title, layer.width, 2);
    const height = padding * 2 + metaLine + gap + lines.length * titleLine;
    if (y + height > bottom) break;
    ctx.globalAlpha = base * 0.08; ctx.fillStyle = layer.color; ctx.fillRect(layer.x, y, layer.width, divider);
    y += padding;
    spacing(0);
    const org = article.org || (naver ? '뉴스' : '정책브리핑');
    ctx.globalAlpha = base; ctx.fillStyle = POLICY_NEWS_ACCENT; ctx.font = `600 ${metaSize}px ${family}`;
    ctx.fillText(org, layer.x, y + metaLine / 2);
    const orgWidth = ctx.measureText(org).width;
    ctx.globalAlpha = base * 0.35; ctx.fillStyle = layer.color; ctx.font = `400 ${metaSize}px ${family}`;
    ctx.fillText(policyNewsTime(article.publishedAt), layer.x + orgWidth + metaSize * 0.5, y + metaLine / 2);
    y += metaLine + gap;
    ctx.globalAlpha = base; ctx.font = `700 ${font}px ${family}`; spacing(-font * 0.015);
    lines.forEach((line, index) => ctx.fillText(line, layer.x, y + index * titleLine + titleLine / 2));
    y += lines.length * titleLine + padding;
  }
  spacing(0);
  const updated = policyNewsTime(data?.updatedAt);
  const footer = [`출처: ${data?.sourceLabel || (naver ? NAVER_NEWS_SOURCE_LABEL : POLICY_NEWS_SOURCE_LABEL)}`, updated ? `${updated} 기준` : '', data?.status === 'stale' ? '갱신 지연' : ''].filter(Boolean).join(' · ');
  ctx.globalAlpha = base * 0.4; ctx.fillStyle = layer.color; ctx.font = `400 ${footerSize}px ${family}`;
  ctx.fillText(fitOne(footer, layer.width), layer.x, layer.y + layer.height - footerHeight / 2);
  ctx.restore();
}
